import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  Users,
  Calendar,
  Filter,
  Search,
  Award,
  Medal,
  Star,
  Shield,
  Home,
  User,
  LogOut,
  Loader,
  Menu,
  X,
} from "lucide-react";
import { generateAvatar } from '../../api/user.api';

const LeaderboardScreen = ({ apiService, userProfile, onLogout }) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaderboardType, setLeaderboardType] = useState("global");
  const [userRank, setUserRank] = useState(null);
  const [userScore, setUserScore] = useState(0);
  const [timeRange, setTimeRange] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // --- Sidebar State & Handlers ---
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [userProfileState, setUserProfileState] = useState(userProfile);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // --- Background Slideshow Logic ---
  const backgroundImages = useMemo(
    () => [
      // Add paths to your background images here.
      // These should be placed in the `public/assets/images/backgrounds/` folder.
      "/assets/images/slideshow/1.png",
      "/assets/images/slideshow/2.png",
      "/assets/images/slideshow/3.png",
      "/assets/images/slideshow/4.png",
      "/assets/images/slideshow/5.png",
      "/assets/images/slideshow/6.png",
      "/assets/images/slideshow/7.png",
      "/assets/images/slideshow/8.png",
    ],
    []
  );

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Store motion directions for each image to keep them consistent
  const motionDirections = useMemo(
    () => backgroundImages.map(() => (Math.random() > 0.5 ? "left" : "right")),
    [backgroundImages]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length
      );
    }, 8000); // Change image every 8 seconds

    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  const BackgroundSlideshow = () => (
    <>
      <style>
        {`
          @keyframes pan-left { 0% { transform: translateX(0%) scale(1.1); } 100% { transform: translateX(-5%) scale(1.1); } }
          @keyframes pan-right { 0% { transform: translateX(-5%) scale(1.1); } 100% { transform: translateX(0%) scale(1.1); } }
          .bg-slideshow-item {
            animation-duration: 12s;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
            animation-direction: alternate;
          }
        `}
      </style>
      {backgroundImages.map((img, index) => (
        <div
          key={img}
          className={`absolute inset-0 w-full h-full bg-cover bg-center transition-opacity duration-2000 ease-in-out bg-slideshow-item ${index === currentImageIndex ? "opacity-100" : "opacity-0"
            }`}
          style={{
            backgroundImage: `url(${img})`,
            animationName:
              motionDirections[index] === "left" ? "pan-left" : "pan-right",
          }}
        />
      ))}
    </>
  );

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setAvatarLoading(true);
    try {
      const data = await generateAvatar(file);
      if (!data.success)
        throw new Error(data.error || "Failed to generate avatar");
      setUserProfileState((prev) => ({ ...prev, avatar_url: data.avatar_url }));
    } catch (error) {
      console.error(error);
    } finally {
      setAvatarLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleLogout = () => setShowLogoutConfirm(true);
  const confirmLogout = () => {
    if (onLogout) onLogout();
    setShowLogoutConfirm(false);
  };

  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        setLoading(true);

        // Map frontend timeRange to backend type parameter
        let backendType = "overall";
        if (timeRange === "week") {
          backendType = "weekly";
        } else if (timeRange === "month") {
          backendType = "monthly";
        }

        // Construct the query string for the API call
        const params = new URLSearchParams({
          type: backendType,
          limit: 100,
        });

        // Add filtering based on leaderboard type
        if (leaderboardType === "department" && userProfile?.data?.department?.name) {
          params.set("department", userProfile.data.department.name);
        } else if (leaderboardType === "role" && userProfile?.data?.role?.name) {
          params.set("role", userProfile.data.role.name);
        }

        const response = await apiService.get(
          `/games/leaderboard?${params.toString()}`
        );

        if (response.success && response.data.leaderboard) {
          // Transform backend structured data to frontend format
          const transformedData = response.data.leaderboard.map(entry => ({
            id: entry.user.id,
            name: entry.user.name,
            initials: entry.user.initials,
            department: entry.user.department,
            role: entry.user.role,
            // Map score based on time range and available stats
            score: entry.stats.total_xp || entry.stats.weekly_xp || entry.stats.monthly_xp || 0,
            badges: entry.stats.badges || 0,
            achievements: entry.stats.achievements || 0,
            averageTime: entry.stats.average_time || 0,
            // Keep original structured data for additional info if needed
            originalStats: entry.stats,
            rank: entry.rank
          }));

          setLeaderboardData(transformedData);

          // Use user_rank from API response (populated from leaderboard_entries)
          // Fallback to finding in the list if not provided
          const apiUserRank = response.data.user_rank;
          if (apiUserRank) {
            setUserRank(apiUserRank);
          } else {
            // Fallback: find the current user's rank in the returned list
            const currentUserRank = transformedData.findIndex(
              (u) => u.id === userProfile?.data?.id
            ) + 1;
            setUserRank(currentUserRank > 0 ? currentUserRank : null);
          }

          // Set the current user's XP score
          const currentUserData = transformedData.find(
            (u) => u.id === userProfile?.data?.id
          );
          const currentUserScore = currentUserData?.score || 0;
          setUserScore(currentUserScore);
        }
      } catch (error) {
        console.error("Error fetching leaderboard data:", error);
        setLeaderboardData([]); // Clear data on error
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboardData();
  }, [apiService, leaderboardType, timeRange, userProfile]);

  // Filter leaderboard data based on search query
  const filteredLeaderboard = leaderboardData.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="relative font-sans text-white min-h-[100dvh] flex overflow-x-hidden">
      <BackgroundSlideshow />
      <div className="absolute inset-0 bg-gradient-to-br from-[#102c4a]/90 to-[#000000]/80 z-0" />

      {/* --- Left Sidebar Navigation --- */}
      <aside className="relative z-10 hidden md:flex w-24 bg-black/30 p-4 flex-col items-center justify-between border-r border-white/10">
        <div>
          <div
            className="mb-12 cursor-pointer"
            onClick={() => navigate("/dashboard")}
          >
            <img
              src={"/assets/images/logo.png"}
              alt="CyberAegis Logo"
              className="w-16 h-16 cursor-pointer hover:border-blue-500 transition-all"
            />
          </div>
          <nav className="flex flex-col items-center gap-8">
            <button
              onClick={() => navigate("/dashboard")}
              title="Home"
              className="p-3 bg-white/5 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Home size={24} />
            </button>
            <button
              onClick={() => navigate("/leaderboard")}
              title="Leaderboard"
              className="p-3 bg-blue-600 rounded-lg"
            >
              <Trophy size={24} />
            </button>
            <button
              onClick={() => navigate("/profile")}
              title="Profile"
              className="p-3 bg-white/5 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <User size={24} />
            </button>
          </nav>
        </div>
        <div className="flex flex-col items-center gap-6">
          <div className="relative">
            <img
              src={
                userProfileState?.avatar_url ||
                userProfile?.data?.avatar_url ||
                "/assets/images/avatar.png"
              }
              alt="User Avatar"
              className="w-12 h-12 rounded-full cursor-pointer border-2 border-gray-600 hover:border-blue-500 transition-all"
              onClick={() => fileInputRef.current?.click()}
            />
            {avatarLoading && (
              <Loader className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-white" />
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={handleLogout}
            title="Logout"
            className="p-3 bg-white/5 rounded-lg hover:bg-red-600 transition-colors"
          >
            <LogOut size={24} />
          </button>
        </div>
      </aside>

      {/* --- Main Content Area --- */}
      <main className="relative z-10 flex-1 flex flex-col p-4 md:p-6 lg:p-8 overflow-y-auto">
        <div className="md:hidden mb-4">
          <div className="flex items-center justify-between rounded-xl bg-black/30 border border-white/10 p-3">
            <button
              onClick={() => setShowMobileNav((prev) => !prev)}
              className="p-2 rounded-lg bg-white/10 hover:bg-blue-600 transition-colors"
              aria-label="Toggle navigation menu"
            >
              {showMobileNav ? <X size={18} /> : <Menu size={18} />}
            </button>
            <h1 className="text-base font-bold">Leaderboard</h1>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg bg-white/10 hover:bg-red-600 transition-colors"
              aria-label="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
          {showMobileNav && (
            <div className="mt-2 grid grid-cols-3 gap-2 rounded-xl bg-black/40 border border-white/10 p-2">
              <button onClick={() => { navigate("/dashboard"); setShowMobileNav(false); }} className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors"><Home size={18} className="mx-auto" /></button>
              <button onClick={() => { navigate("/leaderboard"); setShowMobileNav(false); }} className="p-2 bg-blue-600 rounded-lg"><Trophy size={18} className="mx-auto" /></button>
              <button onClick={() => { navigate("/profile"); setShowMobileNav(false); }} className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors"><User size={18} className="mx-auto" /></button>
            </div>
          )}
        </div>
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-4 mb-4 md:mb-6">
            <h1 className="text-xl md:text-2xl font-bold flex items-center">
              <Trophy className="mr-2 text-yellow-500" size={28} />
              Leaderboard
            </h1>

            <div className="flex items-center space-x-2 md:space-x-4 w-full md:w-auto">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-2.5 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white pl-10 pr-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-64"
                />
              </div>
              <button
                onClick={() => setShowMobileFilters((prev) => !prev)}
                className="md:hidden inline-flex items-center gap-1 px-3 py-2 rounded-md bg-white/10 border border-white/15"
              >
                <Filter size={14} />
                {showMobileFilters ? "Hide" : "Filters"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Filters sidebar */}
            <div className={`${showMobileFilters ? "block" : "hidden"} lg:block lg:col-span-1`}>
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="flex items-center mb-4">
                  <Filter size={16} className="mr-2 text-gray-400" />
                  <h2 className="font-bold">Filters</h2>
                </div>

                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-400 mb-2">
                    Leaderboard Type
                  </h3>
                  <div className="space-y-2">
                    <button
                      className={`w-full text-left px-3 py-2 rounded-md flex items-center transition-all duration-200 ${leaderboardType === "global"
                        ? "bg-green-500/50 text-white shadow-[0_0_8px_#34d399]"
                        : "hover:bg-[rgba(255,255,255,0.1)]"
                        }`}
                      onClick={() => setLeaderboardType("global")}
                    >
                      <Trophy size={16} className="mr-2" />
                      <span>Global</span>
                    </button>

                    <button
                      className={`w-full text-left px-3 py-2 rounded-md flex items-center transition-all duration-200 ${leaderboardType === "department"
                        ? "bg-green-500/50 text-white shadow-[0_0_8px_#34d399]"
                        : "hover:bg-[rgba(255,255,255,0.1)]"
                        }`}
                      onClick={() => setLeaderboardType("department")}
                    >
                      <Users size={16} className="mr-2" />
                      <span>By Department</span>
                    </button>

                    <button
                      className={`w-full text-left px-3 py-2 rounded-md flex items-center transition-all duration-200 ${leaderboardType === "role"
                        ? "bg-green-500/50 text-white shadow-[0_0_8px_#34d399]"
                        : "hover:bg-[rgba(255,255,255,0.1)]"
                        }`}
                      onClick={() => setLeaderboardType("role")}
                    >
                      <Shield size={16} className="mr-2" />
                      <span>By Role</span>
                    </button>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-400 mb-2">
                    Time Range
                  </h3>
                  <div className="space-y-2">
                    <button
                      className={`w-full text-left px-3 py-2 rounded-md flex items-center transition-all duration-200 ${timeRange === "all"
                        ? "bg-green-500/50 text-white shadow-[0_0_8px_#34d399]"
                        : "hover:bg-[rgba(255,255,255,0.1)]"
                        }`}
                      onClick={() => setTimeRange("all")}
                    >
                      <span>All Time</span>
                    </button>

                    <button
                      className={`w-full text-left px-3 py-2 rounded-md flex items-center transition-all duration-200 ${timeRange === "month"
                        ? "bg-green-500/50 text-white shadow-[0_0_8px_#34d399]"
                        : "hover:bg-[rgba(255,255,255,0.1)]"
                        }`}
                      onClick={() => setTimeRange("month")}
                    >
                      <Calendar size={16} className="mr-2" />
                      <span>This Month</span>
                    </button>

                    <button
                      className={`w-full text-left px-3 py-2 rounded-md flex items-center transition-all duration-200 ${timeRange === "week"
                        ? "bg-green-500/50 text-white shadow-[0_0_8px_#34d399]"
                        : "hover:bg-[rgba(255,255,255,0.1)]"
                        }`}
                      onClick={() => setTimeRange("week")}
                    >
                      <Calendar size={16} className="mr-2" />
                      <span>This Week</span>
                    </button>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">
                    Your Stats
                  </h3>
                  <div className="bg-[rgba(255,255,255,0.1)] rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-gray-300">Your Rank</span>
                      <span className="font-bold text-lg">
                        {userRank
                          ? `${userRank}${userRank === 1
                            ? "st"
                            : userRank === 2
                              ? "nd"
                              : userRank === 3
                                ? "rd"
                                : "th"
                          }`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-gray-300">XP Score</span>
                      <span className="font-bold text-lg">
                        {userScore}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Percentile</span>
                      <span className="font-bold text-lg">
                        {userRank && leaderboardData.length > 0
                          ? `Top ${Math.ceil(
                            (userRank / leaderboardData.length) * 100
                          )}%`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main leaderboard */}
            <div className="lg:col-span-3">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg overflow-hidden">
                <div className="p-3 sm:p-4 bg-[rgba(255,255,255,0.1)] border-b border-gray-600 flex justify-between items-center gap-2">
                  <h2 className="font-bold text-sm sm:text-base">
                    {leaderboardType === "global" && "Global Leaderboard"}
                    {leaderboardType === "department" &&
                      `${userProfile?.data?.department?.name || "Your"} Department`}
                    {leaderboardType === "role" &&
                      `${userProfile?.data?.role?.name || "Your"} Role`}
                  </h2>
                  <div className="text-xs sm:text-sm text-gray-400 shrink-0">
                    {timeRange === "all" && "All Time"}
                    {timeRange === "month" && "This Month"}
                    {timeRange === "week" && "This Week"}
                  </div>
                </div>

                {loading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
                  </div>
                ) : (
                  <div>
                    {/* Top 3 podium (for desktop) */}
                    <div className="hidden md:flex justify-center items-end p-6 bg-gradient-to-b from-gray-800 to-gray-850">
                      {filteredLeaderboard.length >= 3 && (
                        <>
                          {/* 2nd place */}
                          <div className="flex flex-col items-center mx-4">
                            <div className="h-20 w-20 rounded-full bg-[rgba(255,255,255,0.1)] border-2 border-gray-500 flex items-center justify-center mb-2">
                              <span className="font-bold text-2xl">
                                {filteredLeaderboard[1].initials}
                              </span>
                            </div>
                            <Medal size={30} className="text-gray-400 mb-1" />
                            <div className="text-center">
                              <div className="font-bold">
                                {filteredLeaderboard[1].name}
                              </div>
                              <div className="text-sm text-gray-400">
                                {filteredLeaderboard[1].score} XP
                              </div>
                            </div>
                            <div className="h-24 w-20 bg-[rgba(255,255,255,0.1)] rounded-t-lg flex items-center justify-center mt-2">
                              <span className="font-bold text-2xl">2</span>
                            </div>
                          </div>

                          {/* 1st place */}
                          <div className="flex flex-col items-center mx-4">
                            <div className="h-24 w-24 rounded-full bg-[rgba(255,255,255,0.1)] border-2 border-yellow-500 flex items-center justify-center mb-2">
                              <span className="font-bold text-3xl">
                                {filteredLeaderboard[0].initials}
                              </span>
                            </div>
                            <Trophy
                              size={36}
                              className="text-yellow-500 mb-1"
                            />
                            <div className="text-center">
                              <div className="font-bold text-lg">
                                {filteredLeaderboard[0].name}
                              </div>
                              <div className="text-sm text-yellow-400">
                                {filteredLeaderboard[0].score} XP
                              </div>
                            </div>
                            <div className="h-32 w-24 bg-yellow-900/50 border border-yellow-700 rounded-t-lg flex items-center justify-center mt-2">
                              <span className="font-bold text-3xl text-yellow-500">
                                1
                              </span>
                            </div>
                          </div>

                          {/* 3rd place */}
                          <div className="flex flex-col items-center mx-4">
                            <div className="h-18 w-18 rounded-full bg-[rgba(255,255,255,0.1)] border-2 border-amber-700 flex items-center justify-center mb-2">
                              <span className="font-bold text-xl">
                                {filteredLeaderboard[2].initials}
                              </span>
                            </div>
                            <Award size={26} className="text-amber-700 mb-1" />
                            <div className="text-center">
                              <div className="font-bold">
                                {filteredLeaderboard[2].name}
                              </div>
                              <div className="text-sm text-gray-400">
                                {filteredLeaderboard[2].score} XP
                              </div>
                            </div>
                            <div className="h-20 w-18 bg-[rgba(255,255,255,0.1)] rounded-t-lg flex items-center justify-center mt-2">
                              <span className="font-bold text-xl">3</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Mobile leaderboard cards */}
                    <div className="md:hidden p-3 space-y-3">
                      {filteredLeaderboard.map((user, index) => {
                        const isCurrentUser = user.id === userProfile?.data?.id;
                        return (
                          <div
                            key={`mobile-${user.id}`}
                            className={`rounded-lg border p-3 ${
                              isCurrentUser
                                ? "bg-blue-900/20 border-blue-500/40"
                                : "bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.12)]"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold">
                                  {user.initials}
                                </div>
                                <div>
                                  <div className={`font-medium text-sm ${isCurrentUser ? "text-blue-400" : ""}`}>
                                    {user.name} {isCurrentUser ? "(You)" : ""}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {user.department || "N/A"} • {user.role || "User"}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-gray-400">Rank</div>
                                <div className="font-bold">{index + 1}</div>
                              </div>
                            </div>
                            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                              <div className="rounded bg-white/5 p-2">
                                <div className="text-gray-400">Score</div>
                                <div className="font-semibold text-yellow-400">{user.score} XP</div>
                              </div>
                              <div className="rounded bg-white/5 p-2">
                                <div className="text-gray-400">Badges</div>
                                <div className="font-semibold">{user.badges}</div>
                              </div>
                              <div className="rounded bg-white/5 p-2">
                                <div className="text-gray-400">Achievements</div>
                                <div className="font-semibold">{user.achievements}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Leaderboard table (tablet/desktop) */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full min-w-[760px]">
                        <thead className="bg-[rgba(255,255,255,0.1)] text-left">
                          <tr>
                            <th className="p-4 font-medium">Rank</th>
                            <th className="p-4 font-medium">User</th>
                            <th className="p-4 font-medium hidden md:table-cell">
                              Department
                            </th>
                            <th className="p-4 font-medium hidden md:table-cell">
                              Role
                            </th>
                            <th className="p-4 font-medium text-right">
                              Score
                            </th>
                            <th className="p-4 font-medium text-right hidden lg:table-cell">
                              Avg Time
                            </th>
                            <th className="p-4 font-medium text-right hidden md:table-cell">
                              Badges
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                          {filteredLeaderboard.map((user, index) => {
                            const isCurrentUser = user.id === userProfile?.data?.id;
                            return (
                              <tr
                                key={user.id}
                                className={`${isCurrentUser
                                  ? "bg-blue-900/20"
                                  : index % 2 === 0
                                    ? "bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]"
                                    : "bg-gray-750"
                                  }`}
                              >
                                <td className="p-4">
                                  {index === 0 ? (
                                    <Trophy
                                      className="text-yellow-500"
                                      size={20}
                                    />
                                  ) : index === 1 ? (
                                    <Medal
                                      className="text-gray-400"
                                      size={20}
                                    />
                                  ) : index === 2 ? (
                                    <Award
                                      className="text-amber-700"
                                      size={20}
                                    />
                                  ) : (
                                    <span
                                      className={`${isCurrentUser
                                        ? "text-blue-400 font-bold"
                                        : ""
                                        }`}
                                    >
                                      {index + 1}
                                    </span>
                                  )}
                                </td>
                                <td className="p-4">
                                  <div className="flex items-center">
                                    <div
                                      className={`h-10 w-10 rounded-full flex items-center justify-center mr-3 text-white ${index === 0
                                        ? "bg-yellow-500"
                                        : index === 1
                                          ? "bg-gray-500"
                                          : index === 2
                                            ? "bg-amber-700"
                                            : isCurrentUser
                                              ? "bg-blue-500"
                                              : "bg-gray-600"
                                        }`}
                                    >
                                      <span className="font-bold text-sm">
                                        {user.initials}
                                      </span>
                                    </div>
                                    <div>
                                      <div
                                        className={`font-medium ${isCurrentUser ? "text-blue-400" : ""
                                          }`}
                                      >
                                        {user.name}
                                      </div>
                                      {isCurrentUser && (
                                        <div className="text-xs text-blue-400">
                                          You
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="p-4 hidden md:table-cell">
                                  {user.department}
                                </td>
                                <td className="p-4 hidden md:table-cell">
                                  <div className="flex items-center">
                                    {user.role === "Admin" ||
                                      user.role === "SystemAdmin" ? (
                                      <span className="bg-red-900/30 text-red-400 text-xs px-2 py-0.5 rounded-full">
                                        Admin
                                      </span>
                                    ) : user.role === "Manager" ||
                                      user.role === "Management" ? (
                                      <span className="bg-purple-900/30 text-purple-400 text-xs px-2 py-0.5 rounded-full">
                                        Manager
                                      </span>
                                    ) : (
                                      <span className="bg-green-900/30 text-green-400 text-xs px-2 py-0.5 rounded-full">
                                        User
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-4 text-right font-bold">
                                  {user.score} XP
                                </td>
                                <td className="p-4 text-right hidden lg:table-cell">
                                  {user.averageTime && user.averageTime > 0
                                    ? `${Math.floor(user.averageTime / 60)}:${String(user.averageTime % 60).padStart(2, '0')}`
                                    : user.averageTime === 0
                                      ? '0:00'
                                      : 'N/A'}
                                </td>
                                <td className="p-4 text-right hidden md:table-cell">
                                  <div className="flex items-center justify-end space-x-1">
                                    {user.originalStats && user.originalStats.badges_list && user.originalStats.badges_list.length > 0 ? (
                                      user.originalStats.badges_list.slice(0, 3).map((badge, bIdx) => (
                                        <div key={bIdx} className="group relative" title={badge.name}>
                                          {badge.icon_url ? (
                                            <img src={badge.icon_url} alt={badge.name} className="w-6 h-6 object-contain" />
                                          ) : (
                                            <Shield size={16} className="text-yellow-500" />
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="flex items-center text-gray-500 text-sm">
                                        <Shield size={14} className="mr-1" /> {user.badges}
                                      </div>
                                    )}
                                    {/* Only show +N if we're truncating the list (assuming we have the list) */}
                                    {user.originalStats && user.originalStats.badges_list && user.originalStats.badges_list.length > 3 && (
                                      <span className="text-xs text-gray-400">+{user.originalStats.badges_list.length - 3}</span>
                                    )}

                                    {/* Keep achievements separate */}
                                    <Star
                                      size={16}
                                      className="ml-3 mr-1 text-yellow-400"
                                    />
                                    <span>{user.achievements}</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* --- Logout Modal --- */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4 border border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Confirm Logout</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to log out?
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="flex-1 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderboardScreen;